import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import useUserStore from '../../store/userStore';
import PopupLoginError from '../Popup/LoginError';
import { useLanguage } from '../../hooks/useLanguage';

function FloatingNavigation() {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const toolbarRef = useRef(null);
    const isScrollingRef = useRef(false); // state 대신 ref 사용
    const [tabName, setTabName] = useState('CINEMA');
    const [initialOffsetTop, setInitialOffsetTop] = useState(null);
    const [activeDot, setActiveDot] = useState(1);
    const currentType = searchParams.get('type') || 'CINEMA';
    const { memberTokens } = useUserStore();
    const [isPopupLoginError, setIsPopupLoginError] = useState(false);
    const [isMainPage, setIsMainPage] = useState(false);
    const [isFolded, setIsFolded] = useState(true);  // 사이드바 접기

    const toggleNavi = () => {
        setIsFolded(prev => !prev);
    };

    useEffect(() => {
        // 현재 경로가 메인 페이지인지 확인
        setIsMainPage(location.pathname === '/main');
    }, [location]);

    useEffect(() => {
        setTabName(currentType);
        setActiveDot(1); // 탭 변경 시 첫 번째 점으로 초기화
    }, [currentType]);


    useEffect(() => {
        if (
            location.pathname === '/main' && 
            !searchParams.get('type') && 
            location.search === ''
        ) {
            navigate({
                pathname: '/main',
                search: '?type=CINEMA'
            }, { replace: true });
        }
    }, [navigate, searchParams, location]);

    // 팝업 확인 버튼 클릭 핸들러 추가
    const handleConfirm = () => {
        navigate('/login');
    };
    
    // 로그인 체크 함수
    const checkLogin = (e) => {
        if (!memberTokens) {
            e.preventDefault();
            setIsPopupLoginError({
                message: t('login_required'),
                action: 'navigate_login'
            });
            return false;
        }
        return true;
    };

    const naviList = [
        { name: t('shopping_cart'), value: 'cart', url: '/order/visit', requireLogin: true },
        { name: t('event'), value: 'event', url: '/community/event', requireLogin: false },
        { name: t('notice'), value: 'notice', url: '/community/notice', requireLogin: false },
        { name: t('production_support'), value: 'supporter', url: '/community/support', requireLogin: false },
        { name: t('inquiry'), value: 'inquiry', url: '/community/inquiry', requireLogin: false },
    ];

    const sectionMapping = useMemo(() => {
        if (currentType === 'CINEMA') {
            return [
                { id: 'main-banner', label: '메인 배너' },
                { id: 'credits-section', label: '크레딧' },
                { id: 'new-arrival', label: 'NEW ARRIVAL' },
                { id: 'cinema-camera', label: 'CINEMA CAMERA' },
                { id: 'cinema-lens', label: 'CINEMA LENS' },
                { id: 'etc-section', label: 'ETC' },
                { id: 'acc-section', label: 'ACCESSORY' }
            ];
        } else {
            return [
                { id: 'main-banner', label: '메인 배너' },
                { id: 'new-arrival', label: 'NEW ARRIVAL' },
                { id: 'camera-section', label: 'CAMERA' },
                { id: 'lens-section', label: 'CINEMA LENS' },
                { id: 'etc-section', label: 'ETC' },
                { id: 'acc-section', label: 'ACCESSORY' }
            ];
        }
    }, [currentType]);

    useEffect(() => {
        const setInitialPosition = () => {
            const naviTop = document.querySelector('.layout_header');
            if (toolbarRef.current && naviTop) {
                const offsetHeight = naviTop.offsetHeight;
                setInitialOffsetTop(offsetHeight);
                
                if (window.scrollY >= offsetHeight) {
                    toolbarRef.current.style.top = '50%';
                    toolbarRef.current.style.transform = 'translateY(-50%)';
                } else {
                    toolbarRef.current.style.transform = '';
                    toolbarRef.current.style.top = `${offsetHeight + 60}px`;
                }
            }
        };

        setInitialPosition();

        const handleScroll = () => {
            if (!initialOffsetTop) {
                setInitialPosition();
                return;
            }

            if (toolbarRef.current) {
                if (window.scrollY >= initialOffsetTop) {
                    toolbarRef.current.style.top = '50%';
                    toolbarRef.current.style.transform = 'translateY(-50%)';
                } else {
                    toolbarRef.current.style.transform = '';
                    toolbarRef.current.style.top = `${initialOffsetTop + 6}px`;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('load', setInitialPosition);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('load', setInitialPosition);
        };
    }, [initialOffsetTop, isMainPage]);

    const moveTop = () => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    };

    const moveBottom = () => {
        window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth'
        });
    };

    const handleTab = (type) => {
        setTabName(type);
        navigate({
            pathname: '/main',
            search: `?type=${type}`
        });
        
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    useEffect(() => {
        if (!isMainPage) return;
        
        const handleScroll = () => {
            // ref로 스크롤 상태 확인
            if (isScrollingRef.current) return;
            
            const scrollPosition = window.scrollY;
            const headerHeight = document.querySelector('.layout_header')?.offsetHeight || 0;
            
            let currentSection = 1;

            if (scrollPosition <= headerHeight + 50) {
                currentSection = 1;
            } else {
                sectionMapping.forEach((section, index) => {
                    const element = document.getElementById(section.id);
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        const elementTop = scrollPosition + rect.top - headerHeight;
                        
                        if (elementTop <= scrollPosition + headerHeight + 100 && 
                            elementTop + rect.height > scrollPosition + headerHeight) {
                            currentSection = index + 1;
                        }
                    }
                });
            }

            setActiveDot(currentSection);
        };

        // 초기 로드 시 첫 번째 점 활성화
        setActiveDot(1);
        
        const initialCheck = setTimeout(() => {
            if (window.scrollY <= 100) {
                setActiveDot(1);
            } else {
                handleScroll();
            }
        }, 300);

        window.addEventListener('scroll', handleScroll);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(initialCheck);
        };
    }, [sectionMapping, isMainPage]); // isScrolling 의존성 제거

    const handleSectionClick = (sectionId) => {
        const sectionIndex = sectionMapping.findIndex(section => section.id === sectionId);
        
        if (sectionIndex === -1) return;

        // ref로 스크롤 상태 설정
        isScrollingRef.current = true;
        
        // 즉시 활성 점 업데이트
        setActiveDot(sectionIndex + 1);
        
        const element = document.getElementById(sectionId);
        
        if (element) {
            const headerHeight = document.querySelector('.layout_header')?.offsetHeight || 0;
            const rect = element.getBoundingClientRect();
            const targetPosition = window.pageYOffset + rect.top - headerHeight - 20;
            
            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
            
            // 스크롤 완료 후 상태 해제
            setTimeout(() => {
                isScrollingRef.current = false;
            }, 1500);
            
        } else {
            isScrollingRef.current = false;
            console.error('섹션을 찾을 수 없습니다:', sectionId);
        }
    };

    // 메인 페이지가 아닌 경우에는 기본 내비게이션만 표시
    if (!isMainPage) {
        return (
            <>
                <aside ref={toolbarRef} className="layout_navi">
                    <nav className={`navi_inner tablet_hide ${isFolded ? 'folded' : ''}`}>
                        {naviList.map((navi, idx) => (
                            <Link
                                key={idx}
                                to={navi.url}
                                className="navi_item flex-center-8 column"
                                onClick={(e) => navi.requireLogin && checkLogin(e, navi.url)}
                            >
                                <i className={`${navi.value}_icon`}></i>
                                <span>{navi.name}</span>
                            </Link>
                        ))}
                        <div className="sns_area">
                            <Link to="https://pf.kakao.com/_iqyas" target="_blank">
                                <div className="sns_area_kakao"></div>
                            </Link>
                            <Link to="https://www.instagram.com/daypay.kr/" target="_blank">
                                <div className="sns_area_insta"></div>
                            </Link>
                            <Link to="https://www.youtube.com/@daypaykr" target="_blank">
                                <div className="sns_area_youtube"></div>
                            </Link>
                        </div>
                    </nav>
                    <div className='tablet_hide navi_open' onClick={toggleNavi}>
                        <button className={isFolded ? '' : 'rotated'}></button>
                        <span>{isFolded ? t('open_txt') : t('close_txt')}</span>
                    </div>
                    <div className="toolbar">
                        <button 
                            className="top_icon" 
                            type="button" 
                            onClick={moveTop}
                        ></button>
                        <button 
                            className="bottom_icon" 
                            type="button" 
                            onClick={moveBottom}
                        ></button>
                    </div>
                </aside>
                
                <PopupLoginError 
                    isPopup={isPopupLoginError} 
                    setIsPopup={setIsPopupLoginError}
                    onConfirm={handleConfirm}
                />
            </>
        );
    }

    return (
        <>
            <aside ref={toolbarRef} className="layout_navi">
                <nav className={`navi_inner tablet_hide ${isFolded ? 'folded' : ''}`}>
                    {naviList.map((navi, idx) => (
                        <Link
                            key={idx}
                            to={navi.url}
                            className="navi_item flex-center-8 column"
                            onClick={(e) => navi.requireLogin && checkLogin(e, navi.url)}
                        >
                            <i className={`${navi.value}_icon`}></i>
                            <span>{navi.name}</span>
                        </Link>
                    ))}
                    <div className="sns_area">
                        <div className="sns_area_kakao"></div>
                        <div className="sns_area_insta"></div>
                        <div className="sns_area_youtube"></div>
                    </div>
                </nav>
                <div className='tablet_hide navi_open' onClick={toggleNavi}>
                    <button className={isFolded ? '' : 'rotated'}></button>
                    <span>{isFolded ? t('open_txt') : t('close_txt')}</span>
                </div>
                <div className="toolbar">
                    <button
                        type="button"
                        className={`right_cinema ${tabName === 'CINEMA' ? 'active' : ''}`}
                        onClick={() => handleTab('CINEMA')}
                    ></button>
                    <button
                        type="button"
                        className={`right_camera ${tabName === 'CAMERA' ? 'active' : ''}`}
                        onClick={() => handleTab('CAMERA')}
                    ></button>
                    <button 
                        className="top_icon" 
                        type="button" 
                        onClick={moveTop}
                    ></button>
                    <button 
                        className="bottom_icon" 
                        type="button" 
                        onClick={moveBottom}
                    ></button>
                </div>
            </aside>

            <aside className="scroll-sidebar tablet_hide">
                <div className="section_area">
                    <div className="section_area_dot">
                        {Array.from({ length: sectionMapping.length }).map((_, idx) => (
                            <div
                                key={idx + 1}
                                className={`dot ${activeDot === idx + 1 ? 'active' : ''}`}
                                data-section={sectionMapping[idx].id}
                                style={{ 
                                    cursor: 'pointer',
                                    pointerEvents: 'auto',
                                    zIndex: 9999,
                                    position: 'relative'
                                }}
                                title={sectionMapping[idx].label}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSectionClick(sectionMapping[idx].id);
                                }}

                            ></div>
                        ))}
                    </div>
                    {/* <button
                        type="button"
                        className={`left_cinema ${tabName === 'CINEMA' ? 'active' : ''}`}
                        onClick={() => handleTab('CINEMA')}
                    ></button>
                    <button
                        type="button"
                        className={`left_camera ${tabName === 'CAMERA' ? 'active' : ''}`}
                        onClick={() => handleTab('CAMERA')}
                    ></button> */}
                </div>
            </aside>
            
            <PopupLoginError 
                isPopup={isPopupLoginError} 
                setIsPopup={setIsPopupLoginError}
                onConfirm={handleConfirm}
            />
        </>
    );
}

export default FloatingNavigation;